import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  MenuItem,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  Tooltip,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as MuiIcons from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import FlagIcon from "@mui/icons-material/Flag";
import PlaceIcon from "@mui/icons-material/Place";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadIcon from "@mui/icons-material/Upload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditWorkOrder from "./EditWorkOrder";
import { PermissionsContext } from "../../../utils/PermissionsContext";
import { useDropzone } from "react-dropzone";
import api, { API_BASE_URL } from "../../../utils/api";

const DetailsTab = ({
  workOrderDetails,
  statuses,
  assignableUsers,
  workOrderTypes,
  workOrderSubtypes,
  workOrderLocations,
  selectedTypeId,
  selectedSubtypeId,
  selectedLocationId,
  selectedPriority,
  selectedAssignee,
  priorityLevels,
  onTypeChange,
  onSubtypeChange,
  onLocationChange,
  onAssigneesChange,
  onPriorityChange,
  calculateDaysOpen,
  setSnackbarMessage,
  setSnackbarOpen,
  setWorkOrderDetails,
  imageURL,
}) => {
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);

  const [previewImageURL, setPreviewImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // ✅ upload states
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: 2.5,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  };

  const innerSoftSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const sectionHeaderIconSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const getStatusObject = () => {
    return statuses.find((s) => s.statusname === workOrderDetails?.status);
  };

  const fetchFiles = useCallback(async () => {
    if (!workOrderDetails) return;

    setLoadingFiles(true);
    try {
      const idToUse =
        workOrderDetails.order_id ||
        workOrderDetails.workorder_id ||
        workOrderDetails.id;

      const res = await api.get(`/workorder-files/${idToUse}`);
      setFiles(res.data || []);
    } catch (err) {
      console.error("Error loading files:", err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [workOrderDetails]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const selectedAssigneesArray =
    Array.isArray(workOrderDetails?.assignees) && workOrderDetails.assignees.length
      ? workOrderDetails.assignees
      : Array.isArray(selectedAssignee)
      ? selectedAssignee
      : selectedAssignee
      ? [selectedAssignee]
      : [];

  const optionsMerged = useMemo(() => {
    const byId = new Map((assignableUsers || []).map((u) => [String(u.UserID), u]));

    (workOrderDetails?.assignees_details || []).forEach((a) => {
      const key = String(a.user_id);
      if (!byId.has(key)) {
        byId.set(key, {
          UserID: a.user_id,
          Name: a.name || "",
          Lastname: a.lastname || "",
          email: a.email || "",
        });
      }
    });

    return Array.from(byId.values());
  }, [assignableUsers, workOrderDetails?.assignees_details]);

  const valueOptions = optionsMerged.filter((u) =>
    (selectedAssigneesArray || []).map(String).includes(String(u.UserID))
  );

  const primaryId =
    (workOrderDetails?.assignees_details || []).find((a) => a.is_primary)?.user_id ??
    (selectedAssigneesArray && selectedAssigneesArray[0]);

  const readOnlyNames = workOrderDetails?.assignees_details?.length
    ? workOrderDetails.assignees_details.map((a) => ({
        id: a.user_id,
        name: `${a.name || ""} ${a.lastname || ""}`.trim(),
        isPrimary: !!a.is_primary,
      }))
    : (selectedAssigneesArray || []).map((id) => {
        const u = optionsMerged.find((x) => String(x.UserID) === String(id));
        return {
          id,
          name: u ? `${u.Name} ${u.Lastname}`.trim() : `#${id}`,
          isPrimary: id === primaryId,
        };
      });

  const SectionTitle = ({ icon, title, action = null }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Box sx={sectionHeaderIconSx}>{icon}</Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  const InfoRow = ({ label, value }) => (
    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
      <strong>{label}:</strong> {value || "N/A"}
    </Typography>
  );

  // ✅ dropzone
  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    setUploadQueue((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "text/plain": [".txt"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  const handleRemoveQueuedFile = (index) => {
    setUploadQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadQueuedFiles = async () => {
    if (!uploadQueue.length || !workOrderDetails) return;

    setUploading(true);

    try {
      const idToUse =
        workOrderDetails.order_id ||
        workOrderDetails.workorder_id ||
        workOrderDetails.id;

      const formData = new FormData();
      formData.append("workorder_id", idToUse);

      uploadQueue.forEach((file) => {
        formData.append("upload_urls", file);
      });

      try {
        await api.post("/workorder-files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (multiErr) {
        // fallback if backend expects single-file route
        for (const file of uploadQueue) {
          const single = new FormData();
          single.append("file", file);

          await api.post(`/workorder-files/${idToUse}`, single, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      setUploadQueue([]);
      await fetchFiles();
      setSnackbarMessage("✅ Files uploaded successfully!");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Upload failed:", err);
      setSnackbarMessage("❌ Failed to upload files.");
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await api.delete(`/workorder-files/${file.id}`);
      await fetchFiles();
      setSnackbarMessage("✅ File deleted successfully!");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Delete failed:", err);
      setSnackbarMessage("❌ Failed to delete file.");
      setSnackbarOpen(true);
    }
  };

 const renderFileCard = (file) => {
  const rawPath = file.file_url || "";
  const normalizedPath = rawPath.startsWith("http")
    ? rawPath
    : `${API_BASE_URL}${rawPath.startsWith("/") ? rawPath : `/${rawPath}`}`;

  const ext = (file.file_name || "").split(".").pop()?.toLowerCase() || "";
  const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  return (
    <Grid item xs={12} sm={6} md={4} key={file.id}>
      <Card
        sx={{
          ...innerSoftSx,
          p: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderRadius: "14px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
        }}
      >
        {/* Top: File Name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={file.file_name}
        >
          {isImage ? (
            <MuiIcons.Image color="primary" fontSize="small" />
          ) : isPdf ? (
            <MuiIcons.PictureAsPdf color="error" fontSize="small" />
          ) : (
            <FolderOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
          )}
          {file.file_name}
        </Typography>

        {/* Middle: Preview Area */}
        {isImage ? (
          <Box
            onClick={() => {
              setPreviewImageURL(normalizedPath);
              setOpenImageModal(true);
            }}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mb: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 220,
                height: 140,
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid rgba(47,99,173,0.12)",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                backgroundColor: "#fff",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              <Box
                component="img"
                src={normalizedPath}
                alt={file.file_name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 140,
              mb: 1.5,
              borderRadius: "10px",
              border: "1px dashed rgba(47,99,173,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            {isPdf ? (
              <MuiIcons.PictureAsPdf color="error" sx={{ fontSize: 46 }} />
            ) : (
              <FolderOutlinedIcon sx={{ fontSize: 46, color: "#2f63ad" }} />
            )}
          </Box>
        )}

        {/* Date / Time */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#6b778c",
            textAlign: "center",
            mb: 1.5,
            fontStyle: "italic",
          }}
        >
          📅{" "}
          {file.uploaded_at
            ? new Date(file.uploaded_at).toLocaleString()
            : "Unknown"}
        </Typography>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          {isImage ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                setPreviewImageURL(normalizedPath);
                setOpenImageModal(true);
              }}
              sx={secondaryButtonSx}
            >
              Preview
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              href={normalizedPath}
              target="_blank"
              rel="noopener noreferrer"
              sx={secondaryButtonSx}
            >
              Open
            </Button>
          )}

          <Button
            size="small"
            variant="contained"
            href={normalizedPath}
            target="_blank"
            rel="noopener noreferrer"
            download
            sx={primaryButtonSx}
          >
            Download
          </Button>
        </Box>

        {/* Delete */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteFile(file)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>
    </Grid>
  );
};

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ ...sectionCardSx, display: "flex", justifyContent: "center" }}>
        <EditWorkOrder
          workOrderDetails={workOrderDetails}
          showAsButton={true}
          onUpdate={(updatedData) => {
            setWorkOrderDetails((prev) => ({ ...prev, ...updatedData }));
            setSnackbarMessage("✅ Status updated successfully!");
            setSnackbarOpen(true);
          }}
        />
      </Card>

      <Box sx={{ textAlign: "center" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#6b778c" }}>
          Work Order ID: <strong>WO-{workOrderDetails?.order_id}</strong>
        </Typography>
      </Box>

      <Grid container rowSpacing={3} columnSpacing={3} sx={{ alignItems: "flex-start" }}>
        {imageURL && (
          <Grid item xs={12}>
            <Card sx={{ ...sectionCardSx, width: "100%", textAlign: "center" }}>
              <SectionTitle
                icon={<InfoOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
                title="Work Order Image"
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 1,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setPreviewImageURL(imageURL);
                  setOpenImageModal(true);
                }}
              >
                <Box
                  component="img"
                  src={imageURL}
                  alt="Work Order"
                  sx={{
                    width: "220px",
                    borderRadius: "10px",
                    border: "2px solid rgba(47,99,173,0.12)",
                    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.08)",
                  }}
                />
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Click image to enlarge
              </Typography>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<CategoryIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Work Order Type"
            />

            {hasPermission("edit_workorder_type") ? (
              <TextField
                select
                label="Work Order Type"
                value={selectedTypeId || ""}
                onChange={(e) => {
                  onTypeChange(e);
                  setSnackbarMessage("✅ Work Order Type updated!");
                  setSnackbarOpen(true);
                }}
                fullWidth
                size="small"
                sx={textFieldSx}
              >
                {workOrderTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.type}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <InfoRow
                label="Type"
                value={workOrderTypes.find((t) => t.id === selectedTypeId)?.type}
              />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<MuiIcons.BuildCircle sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Subtype"
            />

            {hasPermission("edit_workorder_subtype") ? (
              <TextField
                select
                label="Subtype"
                value={selectedSubtypeId || ""}
                onChange={(e) => {
                  onSubtypeChange(e);
                  setSnackbarMessage("✅ Subtype updated!");
                  setSnackbarOpen(true);
                }}
                fullWidth
                size="small"
                sx={textFieldSx}
              >
                {workOrderSubtypes.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.subtype_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <InfoRow
                label="Subtype"
                value={
                  workOrderSubtypes.find((s) => s.id === selectedSubtypeId)?.subtype_name
                }
              />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<PlaceIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Location"
            />

            {hasPermission("edit_workorder_location") ? (
              <TextField
                select
                label="Location"
                value={selectedLocationId || ""}
                onChange={(e) => {
                  onLocationChange(e);
                  setSnackbarMessage("✅ Location updated!");
                  setSnackbarOpen(true);
                }}
                fullWidth
                size="small"
                sx={textFieldSx}
              >
                {workOrderLocations.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.location_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <InfoRow
                label="Location"
                value={
                  workOrderLocations.find((l) => l.id === selectedLocationId)?.location_name
                }
              />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<AssignmentIndIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Assigned To"
            />

            {hasPermission("edit_workorder_assignee") ? (
              <Autocomplete
                multiple
                options={optionsMerged}
                size="small"
                getOptionLabel={(u) => `${u.Name} ${u.Lastname}`.trim()}
                isOptionEqualToValue={(opt, val) =>
                  String(opt.UserID) === String(val.UserID)
                }
                value={valueOptions}
                onChange={(e, newValue) => {
                  const ids = newValue.map((u) => u.UserID);
                  onAssigneesChange(ids);
                  setSnackbarMessage("✅ Assignees updated!");
                  setSnackbarOpen(true);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.UserID}
                      size="small"
                      label={`${option.Name} ${option.Lastname}`.trim()}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign To"
                    placeholder="Select users"
                    sx={textFieldSx}
                  />
                )}
                fullWidth
              />
            ) : (
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Assigned To:</strong>{" "}
                {readOnlyNames.map((x) => (x.isPrimary ? `${x.name} ⭐` : x.name)).join(", ") ||
                  "N/A"}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<FlagIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Priority"
            />

            {hasPermission("edit_workorder_priority") ? (
              <TextField
                select
                label="Priority"
                value={selectedPriority || ""}
                onChange={(e) => {
                  onPriorityChange(e);
                  setSnackbarMessage("✅ Priority updated!");
                  setSnackbarOpen(true);
                }}
                fullWidth
                size="small"
                sx={textFieldSx}
              >
                {priorityLevels.map((priority) => (
                  <MenuItem key={priority.priority_value} value={priority.priority_value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        size="small"
                        sx={{
                          backgroundColor: priority.color_code,
                          color: "#fff",
                          fontWeight: "bold",
                        }}
                        label={priority.priority_value}
                      />
                      {priority.display_name}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <InfoRow
                label="Priority"
                value={
                  priorityLevels.find((p) => p.priority_value === selectedPriority)
                    ?.display_name
                }
              />
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<InfoOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Work Order Details"
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              <InfoRow label="Work Order ID" value={`WO-${workOrderDetails?.order_id}`} />
              <InfoRow label="Property ID" value={workOrderDetails?.property_id} />
              <InfoRow
                label="Created At"
                value={
                  workOrderDetails?.created_at
                    ? new Date(workOrderDetails.created_at).toLocaleString()
                    : "N/A"
                }
              />
              <InfoRow
                label="Days Open"
                value={calculateDaysOpen(workOrderDetails?.created_at)}
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<MuiIcons.CheckCircle sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Status"
            />

            {(() => {
              const statusObj = getStatusObject();
              if (!statusObj) {
                return <Typography variant="body2">Unknown</Typography>;
              }

              const IconComponent = MuiIcons[statusObj.icon] || MuiIcons.Info;
              const iconColor = statusObj.color || "default";

              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconComponent color={iconColor} />
                  <Typography variant="body2">{statusObj.statusname}</Typography>
                </Box>
              );
            })()}
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Description"
            />
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              {workOrderDetails?.description || "No Description Available"}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<MuiIcons.ListAlt sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Details"
            />
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              {workOrderDetails?.details || "No Details Available"}
            </Typography>
          </Card>
        </Grid>

        {/* ✅ FILES WITH UPLOAD */}
        <Grid item xs={12}>
          <Card sx={sectionCardSx}>
            <SectionTitle
              icon={<FolderOutlinedIcon sx={{ color: "#2f63ad", fontSize: 18 }} />}
              title="Files"
            />

            <Typography variant="body2" sx={{ mb: 1.25, color: "#6b778c" }}>
              Attach files to this work order
            </Typography>

            <Box
              {...getRootProps()}
              sx={{
                ...innerSoftSx,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#e3f2fd" : "rgba(47,99,173,0.02)",
                mb: 2,
                borderStyle: "dashed",
                borderWidth: "2px",
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? "Drop files here..."
                  : "Drag & drop multiple files here, or click to select"}
              </Typography>
            </Box>

            {uploadQueue.length > 0 && (
              <Box
                sx={{
                  ...innerSoftSx,
                  p: 1,
                  maxHeight: 150,
                  overflowY: "auto",
                  mb: 2,
                }}
              >
                {uploadQueue.map((file, idx) => (
                  <Box
                    key={`${file.name}-${idx}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      borderBottom:
                        idx !== uploadQueue.length - 1
                          ? "1px solid rgba(47,99,173,0.08)"
                          : "none",
                      py: 0.75,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: "80%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </Typography>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveQueuedFile(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleUploadQueuedFiles}
                disabled={uploading || uploadQueue.length === 0}
                startIcon={uploading ? null : <UploadIcon />}
                sx={primaryButtonSx}
              >
                {uploading ? "Uploading..." : `Upload ${uploadQueue.length || ""}`.trim()}
              </Button>

              {uploadQueue.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setUploadQueue([])}
                  disabled={uploading}
                  sx={secondaryButtonSx}
                >
                  Clear
                </Button>
              )}
            </Box>

            {loadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : files.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                No files attached.
              </Typography>
            ) : (
           <Grid
  container
  rowSpacing={5}
  columnSpacing={3}
  sx={{
    alignItems: "stretch",
  }}
>
  {files.map(renderFileCard)}
</Grid>
            )}
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            backgroundColor: "#111",
            p: 2,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box
            component="img"
            src={previewImageURL}
            alt="Enlarged Attachment"
            sx={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "10px",
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DetailsTab;